import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { observable, reaction, action, computed, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import Bind from 'lodash-decorators/bind';

import messages from './MediaAboutUsView.intl';

import styles from './MediaAboutUsView.module.scss';

import { LOAD_STATES, ANCHOR } from 'common/consts/common';

import { Container, Title } from 'common/components/Layout/Layout';
import PrimaryButtonComponent from 'common/components/PrimaryButtonComponent/PrimaryButtonComponent';
import CategoryList from 'common/components/CategoryList/CategoryList';
import NewsBig from './components/NewsBig/NewsBig';
import NewsSmall from './components/NewsSmall/NewsSmall';
import LanguageService from 'services/Language.service';
import PressArticleStore from 'stores/PressArticle.store';
import CompaniesStore from 'stores/Companies';
import Placeholder from 'common/components/Placeholder/Placeholder';
import ContentBar from 'common/components/Placeholder/components/ContentBar/ContentBar';
import PageNotFoundView from 'views/PageNotFound/PageNotFoundView';

import { isOnMobile } from 'utils/device-utils';
import MediaAboutUsPlaceholder from './placeholder/MediaAboutUsPlaceholder';

const TOP_ARTICLES_LIMIT = 6;

@injectIntl
@withRouter
@observer
export default class MediaAboutUsView extends Component {
  criteria = observable({
    page: 1,
    category: this.props.match.params.category || ''
  });
  articles = observable([]);
  loadState = observable.box('');
  isExploited = observable.box(false);
  companies = CompaniesStore.companies;
  currentLanguage = LanguageService.getLang();

  async componentDidMount() {
    reaction(() => this.criteria.category, () => this.articles.clear());

    reaction(() => this.criteriaKey, () => this.getArticles(this.criteria), {
      fireImmediately: true
    });
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { category } = this.props.match.params;

    if (category !== prevProps.match.params.category) {
      this.updateCriteria({
        category: category || '',
        page: 1
      });
    }
  }

  @computed
  get criteriaKey() {
    return JSON.stringify(this.criteria);
  }

  @computed
  get isLoaded() {
    return this.loadState.get() === LOAD_STATES.LOADED;
  }

  @computed
  get latestArticle() {
    return this.articles.slice(0, TOP_ARTICLES_LIMIT);
  }

  @computed
  get otherArticle() {
    return this.articles.slice(TOP_ARTICLES_LIMIT);
  }

  @computed
  get categoryList() {
    const defaultOption = {
      text: this.props.intl.formatMessage(messages.taxonomyAll),
      value: ''
    };

    const companiesOptions = this.companies.map(c => ({
      text: c.name,
      value: c.codename.replace(/_/g, '-')
    }));

    return [defaultOption, ...companiesOptions];
  }

  @action
  updateCriteria(newCriteria) {
    Object.assign(this.criteria, newCriteria);
  }

  @action
  async getArticles(criteria) {
    this.loadState.set(LOAD_STATES.LOADING);
    const { data, isExploited } = await PressArticleStore.fetchArticles(
      criteria
    );
    runInAction(() => {
      this.articles.push(...data);
      this.loadState.set(LOAD_STATES.LOADED);
      this.isExploited.set(isExploited);
    });
  }

  @Bind()
  async showMore() {
    this.updateCriteria({
      page: this.criteria.page + 1
    });
  }

  @Bind()
  onCategoryChange(category) {
    const {
      history,
      match: {
        params: { lang }
      }
    } = this.props;

    const langPrefix = lang ? `/${lang}` : '';
    history.push(
      `${langPrefix}${ANCHOR.MEDIAABOUTUS[this.currentLanguage]}/${category}`
    );
  }

  render() {
    const {
      criteria: { category },
      isExploited
    } = this;

    const isValidCategory =
      category === '' ||
      this.companies.find(
        company => company.slug === this.props.match.params.category
      );

    if (!isValidCategory && this.companies.length) {
      return <PageNotFoundView />;
    }

    return (
      <div>
        <Container>
          <Title title={this.props.intl.formatMessage(messages.heading)} />
        </Container>
        <Container className={styles.Container}>
          <div className={styles.ArticlesColumn}>
            <Placeholder
              component={<MediaAboutUsPlaceholder />}
              ready={this.isLoaded || !!this.articles.length}
            >
              <h2 className={styles.LastAddedTitle}>
                {this.props.intl.formatMessage(messages.lastAdded)}
              </h2>
              {this.isLoaded &&
                !this.articles.length && (
                  <p className={styles.NoResults}>
                    {this.props.intl.formatMessage(messages.nothingToShow)}
                  </p>
                )}
              <div className={styles.RecentArticles}>
                {this.latestArticle.map(article => (
                  <NewsBig key={article.id} pressArticle={article} />
                ))}
              </div>
              {this.otherArticle.length > 0 && (
                <div>
                  <h2 className={styles.LastAddedTitle}>
                    {this.props.intl.formatMessage(messages.earlier)}
                  </h2>
                  {this.otherArticle.map(article => (
                    <NewsSmall
                      pressArticle={article}
                      key={article.id}
                      className={styles.OldNews}
                    />
                  ))}
                </div>
              )}
              {!isExploited.get() && (
                <div className={styles.PrimaryButtonStyle}>
                  <PrimaryButtonComponent
                    className={styles.PrimaryButton}
                    text={this.props.intl.formatMessage(messages.showMore)}
                    onClick={this.showMore}
                    isBusy={!this.isLoaded}
                  />
                </div>
              )}
            </Placeholder>
          </div>
          <div className={styles.SidebarCategories}>
            <h3 className={styles.CategoryTitle}>
              {this.props.intl.formatMessage(messages.categories)}
            </h3>
            <Placeholder
              component={
                <ContentBar className={styles.ContentBarPlaceholder} />
              }
              ready={this.categoryList.length > 1}
              repeat={isOnMobile() ? 1 : 6}
            >
              <CategoryList
                categories={this.categoryList}
                onSelect={this.onCategoryChange}
                activeOption={category}
              />
            </Placeholder>
          </div>
        </Container>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/views/MediaAboutUs/MediaAboutUsView.js